import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast, ToastContainer } from 'react-toastify';
import z from 'zod';
import { createUserAddress, getReturnMailReason } from '../api/Network';
import FormInput from '../components/FormsInput/FormInput';
import { FormSelect } from '../components/FormsInput/FormSelect';

export const CreateRecordSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  companyName: z.string().nullable().optional(),
  addressLine1: z.string().min(1, 'Address Line 1 is required'),
  addressLine2: z.string().nullable().optional(),
  city: z.string().min(1, 'City is required'),
  state: z.string().nullable().optional(),
  zip: z.string().min(1, 'Zip is required'),
  country: z.string().min(1, 'Country is required'),
  documentType: z.string().nullable().optional(),
  fileName: z.string().nullable().optional(),
  mailed: z.boolean().default(false),
  mailedDate: z.string().nullable().optional(),
  logicAppLastRunId: z.string().nullable().optional(),
  intelligentMailBarcodeDigits: z.string().nullable().optional(),
  isProcessed: z.boolean().default(false),
  reason: z.string().nullable().optional(),
  returnDate: z.string().nullable().optional(),
  returnReason: z.string().nullable().optional(),
  isDuplicate: z.boolean().default(false),
  deliveryAddress: z.string().nullable().optional(),
  deliveryCity: z.string().nullable().optional(),
  deliveryState: z.string().nullable().optional(),
  deliveryZip: z.string().nullable().optional(),
  alternateAddress: z.string().nullable().optional(),
  createdByApimUsername: z.string().optional(),
  modifiedByApimUsername: z.string().nullable().optional(),
  modifiedBy: z.string().nullable().optional(),
  modifiedDate: z.string().nullable().optional(),
  returnMailReasonId: z.number().nullable().optional(),
});
export type CreateRecordFormData = z.infer<typeof CreateRecordSchema>;

export const CreateRecordModal = () => {
  const queryClient = useQueryClient();

  const { data: reasons } = useQuery(['reasons'], () => getReturnMailReason(), {
    onError: (error: Error) => toast.error(error.message),
    refetchOnWindowFocus: false,
    refetchInterval: 3600000,
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, touchedFields, isValid },
  } = useForm<CreateRecordFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      companyName: null,
      addressLine1: '',
      addressLine2: null,
      city: '',
      state: null,
      zip: '',
      country: '',
      documentType: null,
      fileName: null,
      mailed: false,
      mailedDate: null,
      logicAppLastRunId: null,
      intelligentMailBarcodeDigits: null,
      isProcessed: false,
      reason: null,
      returnDate: null,
      returnReason: null,
      isDuplicate: false,
      deliveryAddress: null,
      deliveryCity: null,
      deliveryState: null,
      deliveryZip: null,
      alternateAddress: null,
      createdByApimUsername: '',
      modifiedByApimUsername: null,
      modifiedBy: null,
      modifiedDate: null,
      returnMailReasonId: null,
    },
    mode: 'onTouched',
  });

  const closeModal = (e?: React.MouseEvent<HTMLButtonElement | HTMLFormElement>) => {
    e?.preventDefault();
    (document.getElementById('create_record_modal') as HTMLFormElement)?.close();
    reset();
  };

  const onSubmit = async (data: CreateRecordFormData) => {
    // Find the ID from the return reason name
    const returnReasonObj = reasons?.data.find((reason) => reason.name === data.returnReason);
    const finalizeData = {
      ...data,
      reason: data.returnReason,
      returnMailReasonId: returnReasonObj?.id,
    };
    createRecord(finalizeData);
  };

  const { mutate: createRecord, isLoading: isCreateLoading } = useMutation(
    (data: CreateRecordFormData) => {
      return createUserAddress(data);
    },
    {
      onSuccess: async (response) => {
        toast.success('Successfully created record');
        await queryClient.invalidateQueries(['userAddresses']);
        reset();
        closeModal();
      },
      onError: async (error: Error) => {
        toast.error(error.message);
      },
    },
  );

  return (
    <dialog className="modal" id="create_record_modal">
      <div className="modal-box w-full max-w-6xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div>
          <div className="text text-primary text-2xl">Create Record</div>
          <div className="divider divider-primary m-0"></div>
        </div>

        {/* Form - Scrollable Content */}
        <form id="create-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6 overflow-y-auto flex-1 px-4">
          {/* Identity */}
          <div className="rounded-box border border-base-300 p-4">
            <div className="text-xl font-semibold text-primary">Identity</div>
            <div className="divider divider-primary my-2"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <FormInput
                label="First Name"
                name="firstName"
                type="text"
                register={register('firstName', { required: 'First name is required' })}
                errors={errors.firstName}
                touchedField={touchedFields.firstName}
                placeholder="first name"
                required={true}
              />
              <FormInput
                label="Last Name"
                name="lastName"
                type="text"
                register={register('lastName', { required: 'Last name is required' })}
                errors={errors.lastName}
                touchedField={touchedFields.lastName}
                placeholder="last name"
                required={true}
              />
              <FormInput
                label="Company Name"
                name="companyName"
                type="text"
                register={register('companyName')}
                errors={errors.companyName}
                touchedField={touchedFields.companyName}
                placeholder="company name"
                required={false}
              />
            </div>
          </div>
          {/* Address */}
          <div className="rounded-box border border-base-300 p-4">
            <div className="text-xl text-primary font-semibold ">Address</div>
            <div className="divider divider-primary my-2"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <FormInput
                label="Address Line 1"
                name="addressLine1"
                type="text"
                register={register('addressLine1', { required: 'Address Line 1 is required' })}
                errors={errors.addressLine1}
                touchedField={touchedFields.addressLine1}
                placeholder="address line 1"
                required={true}
              />
              <FormInput
                label="Address Line 2"
                name="addressLine2"
                type="text"
                register={register('addressLine2')}
                errors={errors.addressLine2}
                touchedField={touchedFields.addressLine2}
                placeholder="address line 2"
                required={false}
              />
              <FormInput
                label="City"
                name="city"
                type="text"
                register={register('city', { required: 'City is required' })}
                errors={errors.city}
                touchedField={touchedFields.city}
                placeholder="city"
                required={true}
              />
              <FormInput
                label="State"
                name="state"
                type="text"
                register={register('state', { required: 'State is required' })}
                errors={errors.state}
                touchedField={touchedFields.state}
                placeholder="state"
                required={true}
              />
              <FormInput
                label="Zip"
                name="zip"
                type="text"
                register={register('zip', { required: 'Zip is required' })}
                errors={errors.zip}
                touchedField={touchedFields.zip}
                placeholder="zip"
                required={true}
              />
              <FormInput
                label="Country"
                name="country"
                type="text"
                register={register('country', { required: 'Country is required' })}
                errors={errors.country}
                touchedField={touchedFields.country}
                placeholder="country"
                required={true}
              />
            </div>
          </div>
          {/* Document */}
          <div className="rounded-box border border-base-300 p-4">
            <div className="text-xl text-primary font-semibold ">Document</div>
            <div className="divider divider-primary my-2"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <FormInput
                label="Document Type"
                name="documentType"
                type="text"
                register={register('documentType')}
                errors={errors.documentType}
                touchedField={touchedFields.documentType}
                placeholder="document type"
                required={false}
              />
              <FormInput
                label="File Name"
                name="fileName"
                type="text"
                register={register('fileName')}
                errors={errors.fileName}
                touchedField={touchedFields.fileName}
                placeholder="file name"
                required={false}
              />
              <FormInput
                label="Barcode Digits"
                name="intelligentMailBarcodeDigits"
                type="text"
                register={register('intelligentMailBarcodeDigits')}
                errors={errors.intelligentMailBarcodeDigits}
                touchedField={touchedFields.intelligentMailBarcodeDigits}
                placeholder="barcode digits"
                required={false}
              />
            </div>
          </div>
          {/* Mail Status */}
          <div className="rounded-box border border-base-300 p-4">
            <div className="text-xl text-primary  font-semibold ">Mail Status</div>
            <div className="divider divider-primary my-2"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <FormInput
                label="Mailed"
                name="mailed"
                type="checkbox"
                register={register('mailed')}
                errors={errors.mailed}
                touchedField={touchedFields.mailed}
              />
              <FormInput
                label="Mailed Date"
                name="mailedDate"
                type="datetime-local"
                register={register('mailedDate')}
                errors={errors.mailedDate}
                touchedField={touchedFields.mailedDate}
                placeholder="mailed date"
                required={false}
              />
              <FormInput
                label="Return Date"
                name="returnDate"
                type="datetime-local"
                register={register('returnDate')}
                errors={errors.returnDate}
                touchedField={touchedFields.returnDate}
                placeholder="return date"
                required={false}
              />

              <FormSelect
                label="Return Reason"
                name="returnReason"
                register={register('returnReason')}
                errors={errors.returnReason}
                touchedField={touchedFields.returnReason}
                placeholder="Select return reason"
                list={reasons?.data ?? []}
              />

              <FormInput
                label="Logic App Last Run Id"
                name="logicAppLastRunId"
                type="text"
                register={register('logicAppLastRunId')}
                errors={errors.logicAppLastRunId}
                touchedField={touchedFields.logicAppLastRunId}
                placeholder="logic app last run id"
                required={false}
              />
            </div>
          </div>
          {/* Delivery */}
          <div className="rounded-box border border-base-300 p-4">
            <div className="text-xl text-primary font-semibold ">Delivery</div>
            <div className="divider divider-primary my-2"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <FormInput
                label="Delivery Address"
                name="deliveryAddress"
                type="text"
                register={register('deliveryAddress')}
                errors={errors.deliveryAddress}
                touchedField={touchedFields.deliveryAddress}
                placeholder="delivery address"
                required={false}
              />
              <FormInput
                label="Delivery City"
                name="deliveryCity"
                type="text"
                register={register('deliveryCity')}
                errors={errors.deliveryCity}
                touchedField={touchedFields.deliveryCity}
                placeholder="delivery city"
                required={false}
              />
              <FormInput
                label="Delivery State"
                name="deliveryState"
                type="text"
                register={register('deliveryState')}
                errors={errors.deliveryState}
                touchedField={touchedFields.deliveryState}
                placeholder="delivery state"
                required={false}
              />
              <FormInput
                label="Delivery Zip"
                name="deliveryZip"
                type="text"
                register={register('deliveryZip')}
                errors={errors.deliveryZip}
                touchedField={touchedFields.deliveryZip}
                placeholder="delivery zip"
                required={false}
              />
              <FormInput
                label="Alternate Address"
                name="alternateAddress"
                type="text"
                register={register('alternateAddress')}
                errors={errors.alternateAddress}
                touchedField={touchedFields.alternateAddress}
                placeholder="alternate address"
                required={false}
              />
            </div>
          </div>
          {/* Flags */}
          <div className="rounded-box border border-base-300 p-4">
            <div className="text-xl text-primary font-semibold ">Flags</div>
            <div className="divider divider-primary my-2"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <FormInput
                label="Is Duplicate"
                name="isDuplicate"
                type="checkbox"
                register={register('isDuplicate')}
                errors={errors.isDuplicate}
                touchedField={touchedFields.isDuplicate}
              />
              <FormInput
                label="Is Processed"
                name="isProcessed"
                type="checkbox"
                register={register('isProcessed')}
                errors={errors.isProcessed}
                touchedField={touchedFields.isProcessed}
              />
            </div>
          </div>
        </form>

        {/* Fixed Action Buttons */}
        <div className="modal-action border-t border-base-300 mt-6 pt-4">
          <button type="submit" form="create-form" className="btn btn-success" disabled={isCreateLoading || !isValid}>
            <div>Create</div>
          </button>
          <button className="btn btn-error" onClick={closeModal}>
            Cancel
          </button>
        </div>

        <div className="fixed inset-0 pointer-events-none">
          <div className="pointer-events-auto">
            <ToastContainer containerId={'modal-plant'} position="top-center" />
          </div>
        </div>
      </div>

      <form method="dialog" className="modal-backdrop absolute w-full h-full" onClick={closeModal}>
        <button>close</button>
      </form>
    </dialog>
  );
};
